import {
  ItemsContext_MultiOrderGuideItemsQuery,
  ItemsContext_MultiOrderGuideItemsQueryVariables,
} from '@/types';
import { MULTI_ORDER_GUIDE_QUERY } from '@/contexts/items-context-queries';
import {
  OfflineBackgroundSyncRegistryAdapter,
  OfflineOrderGuideAdapter,
} from '@/adapters/offline-mode';
import { OfflineSyncType } from '@/utils/offline-mode';
import { print as printQuery } from 'graphql';
import { useGetDynamicConfigValueForChat } from '@/hooks/use-dynamic-config-value';
import { useItemQueryLocale } from '@/hooks/use-item-query-variables';
import { useLazyQuery } from '@/hooks/use-lazy-query';

/**
 * Custom hook to download and save the order guide for a specific restaurant and supplier.
 *
 * This hook uses a lazy query to fetch the order guide from the server and then saves it
 * locally using the OfflineOrderGuideAdapter.
 *
 * @returns {Object} - An object containing the downloadOrderGuide function.
 *
 * @function
 * @name useDownloadOrderGuide
 *
 * @example
 * const { downloadOrderGuide } = useDownloadOrderGuide();
 *
 * // Download and save order guide for a specific restaurant and supplier
 * downloadOrderGuide('restaurant-uuid', 'chat-uuid', 'supplier-uuid');
 */
export const useDownloadOrderGuide = () => {
  const locale = useItemQueryLocale();
  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();

  const getOrderGuide = useLazyQuery<
    ItemsContext_MultiOrderGuideItemsQuery,
    ItemsContext_MultiOrderGuideItemsQueryVariables
  >(MULTI_ORDER_GUIDE_QUERY, {
    fetchPolicy: 'network-only',
  });

  /**
   * Fetches and saves the order guide for a given restaurant and supplier.
   *
   * @async
   * @param {string} restaurantUUID - The UUID of the restaurant.
   * @param {string} chatUUID - The UUID of the chat between the restaurant and supplier.
   * @param {string} supplierUUID - The UUID of the supplier.
   */
  const downloadOrderGuide = async (
    restaurantUUID: string,
    chatUUID: string,
    supplierUUID: string,
  ) => {
    // TODO: use salt cache once we decide on the path forward for loading
    // SALT data for the OG. Until then, this will ensure cached data matches
    // what's already live and verified, and the dynamic_config flag can act
    // as a killswitch

    const saltApiIntegrationEnabled = getDynamicConfigValueForChat(
      chatUUID,
      'salt_api_integration_enabled',
    );

    const variables = {
      locale,
      restaurantUUID,
      skipGroupMemberships: !getDynamicConfigValueForChat(
        chatUUID,
        'item_groups_enabled',
      ),
      skipLiveSalt: !saltApiIntegrationEnabled,
      skipSaltApi: !saltApiIntegrationEnabled,
      supplierUUID,
      useSaltCache: false,
    };

    const { data: orderGuideData } = await getOrderGuide({
      context: {
        offlineSync: true,
      },
      variables,
    });
    const offlineOrderGuideAdapter = new OfflineOrderGuideAdapter(
      restaurantUUID,
      supplierUUID,
    );
    await offlineOrderGuideAdapter.save(orderGuideData);

    const backgroundSyncRegistryAdapter =
      new OfflineBackgroundSyncRegistryAdapter();
    await backgroundSyncRegistryAdapter.updateSyncMetadata(
      restaurantUUID,
      supplierUUID,
      {
        query: printQuery(MULTI_ORDER_GUIDE_QUERY),
        syncType: OfflineSyncType.OrderGuide,
        variables,
      },
    );
  };

  return { downloadOrderGuide };
};
